import { useState } from "react";
import { AlertTriangle, Moon, Merge } from "lucide-react";
import { useLoad } from "@/lib/api";
import { PageHead, card, Sel, Empty } from "@/components/admin/kit";
import { LiveCalls } from "@/components/admin/LiveCalls";
import { CallActions } from "@/components/admin/CallPreview";

const TONE = { daily: "bg-[var(--lav-2)] text-[var(--violet)]", welcome: "bg-green-100 text-green-800", refill: "bg-orange-100 text-orange-700", followup: "bg-sky-100 text-sky-800", dental: "bg-pink-100 text-pink-800" };

function Row({ r, reload }) {
  return (
    <div className="grid gap-2 border-b border-[var(--lav-2)] py-3 text-sm last:border-0 md:grid-cols-[70px_1fr_auto]" data-testid={`plan-row-${r.date}-${r.time}-${r.phone}`}>
      <b className="tabular-nums">{r.time}</b>
      <div>
        <p className="font-semibold">{r.receiver} <span className="font-normal text-muted-foreground">· {r.phone} · voice {r.voice}</span></p>
        {r.items.map((it, i) => (
          <p key={i} className="mt-1 flex flex-wrap items-center gap-2"><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${TONE[it.kind]}`}>{it.label}</span><span>{it.patient}</span><span className="text-xs text-muted-foreground">{it.detail}</span>{it.skipped && <span className="rounded-full bg-gray-200 px-2 text-[11px] font-bold">skipped</span>}</p>
        ))}
      </div>
      <div className="flex flex-wrap items-start gap-1 text-[11px] font-bold">
        {r.merged && <span className="flex items-center gap-1 rounded-full bg-[var(--lav-2)] px-2 py-0.5" data-testid="plan-merged"><Merge size={12} /> merged into 1 call</span>}
        {r.quiet && <span className="flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-purple-800" data-testid="plan-quiet"><Moon size={12} /> quiet hours</span>}
        {r.over_limit && <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-red-700" data-testid="plan-over-limit"><AlertTriangle size={12} /> {r.calls_that_day} calls that day</span>}
      </div>
      <CallActions r={r} reload={reload} tid={`plan-${r.date}-${r.time}-${r.items[0]?.patient_id?.slice(0, 6)}`} />
    </div>
  );
}

export default function CallSchedule() {
  const [days, setDays] = useState("7");
  const [rows, reload] = useLoad(`/call-schedule?days=${days}`, [days]);
  const byDate = (rows || []).reduce((acc, r) => ({ ...acc, [r.date]: [...(acc[r.date] || []), r] }), {});
  return (
    <div data-testid="call-schedule-page">
      <PageHead title="Call plan" sub="Which call reaches which phone, and when. Calls to the same phone at the same time merge into one. With Automatic calls ON, the scheduler places them on time.">
        <div className="w-40"><Sel value={days} onChange={setDays} options={[["3", "Next 3 days"], ["7", "Next 7 days"], ["14", "Next 14 days"]]} testid="plan-days-select" /></div>
      </PageHead>
      <LiveCalls />
      {!rows ? <Empty>Loading…</Empty> : !rows.length ? <Empty>No calls planned.</Empty> : Object.entries(byDate).map(([d, list]) => (
        <div key={d} className={`${card} mb-4`} data-testid={`plan-day-${d}`}>
          <p className="mb-1 font-bold">{new Date(d).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short" })} <span className="text-xs font-normal text-muted-foreground">· {list.length} calls</span></p>
          {list.map((r, i) => <Row key={`${r.time}-${i}`} r={r} reload={reload} />)}
        </div>
      ))}
    </div>
  );
}
